import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";
import {
  AMBER_INK,
  AMBER_SOFT_BG,
  GREEN_INK,
  GREEN_SOFT_BG,
} from "apps/project-iq/utils/diagnosticUtils";

const STEP_BADGE_SIZE = "28px";
const PROGRESS_BAR_HEIGHT = "8px";
const PROGRESS_TRANSITION = "width 0.5s ease";

export const useAnalyticsRunStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
    paddingBottom: designTokens.spacing[8],
  },

  panel: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: designTokens.spacing[6],
    fontFamily: projectIqTokens.fontFamily,
  },

  panelHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: designTokens.spacing[3],
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
  },

  panelTitle: {
    fontSize: designTokens.fontSize.xl,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: "0",
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: designTokens.fontSize.xxs,
    fontWeight: 800,
    letterSpacing: designTokens.letterSpacing.wide,
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: "10px",
    paddingRight: "10px",
    borderRadius: "6px",
    textTransform: "uppercase",
  },

  statusBadgeScored: {
    backgroundColor: GREEN_SOFT_BG,
    color: GREEN_INK,
  },

  statusBadgeInProgress: {
    backgroundColor: AMBER_SOFT_BG,
    color: AMBER_INK,
  },

  statusBadgeError: {
    backgroundColor: projectIqTokens.rustBg,
    color: projectIqTokens.rust,
  },

  stepList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    marginBottom: designTokens.spacing[5],
  },

  stepRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[3],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[4],
    borderTop: `1px solid ${projectIqTokens.line}`,
    borderRight: `1px solid ${projectIqTokens.line}`,
    borderBottom: `1px solid ${projectIqTokens.line}`,
    borderLeft: `3px solid ${projectIqTokens.violet}`,
    borderRadius: designTokens.borderRadius.md,
  },

  stepBadge: {
    flexShrink: 0,
    width: STEP_BADGE_SIZE,
    height: STEP_BADGE_SIZE,
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.muted,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  stepContent: {
    flex: 1,
    minWidth: 0,
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    margin: "0",
    lineHeight: "1.5",
    "& strong": {
      color: projectIqTokens.ink,
      fontWeight: designTokens.fontWeight.semibold,
    },
  },

  stepPill: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.bold,
    letterSpacing: designTokens.letterSpacing.wide,
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    borderRadius: designTokens.borderRadius.full,
    textTransform: "uppercase" as const,
    backgroundColor: GREEN_SOFT_BG,
    color: GREEN_INK,
    whiteSpace: "nowrap" as const,
  },

  progressSection: {
    marginBottom: designTokens.spacing[4],
  },

  progressBar: {
    height: PROGRESS_BAR_HEIGHT,
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: projectIqTokens.line,
    overflow: "hidden",
    marginBottom: designTokens.spacing[2],
  },

  progressFill: {
    height: "100%",
    backgroundColor: projectIqTokens.teal,
    borderRadius: designTokens.borderRadius.full,
    transition: PROGRESS_TRANSITION,
  },

  progressMeta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
  },

  divider: {
    borderTop: `1px solid ${projectIqTokens.line}`,
    borderRight: "none",
    borderBottom: "none",
    borderLeft: "none",
    marginTop: designTokens.spacing[4],
    marginBottom: designTokens.spacing[4],
    marginLeft: "0",
    marginRight: "0",
  },

  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[4],
    flexWrap: "wrap",
  },

  footerText: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    flex: 1,
  },

  footerActions: {
    display: "flex",
    gap: designTokens.spacing[2],
    flexShrink: 0,
  },

  // Scoring history table
  historyTable: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontFamily: projectIqTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
  },

  historyThead: {
    borderBottom: `2px solid ${projectIqTokens.line}`,
  },

  historyTh: {
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    textAlign: "left" as const,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
    textTransform: "uppercase" as const,
    letterSpacing: designTokens.letterSpacing.wide,
  },

  historyTd: {
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[3],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    borderBottom: `1px solid ${projectIqTokens.line}`,
    color: projectIqTokens.ink,
    fontSize: designTokens.fontSize.sm,
  },

  historyEmpty: {
    paddingTop: designTokens.spacing[5],
    paddingBottom: designTokens.spacing[5],
    textAlign: "center" as const,
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    fontStyle: "italic",
  },

  // Status pills for history rows — base + color variants
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.bold,
    letterSpacing: designTokens.letterSpacing.wide,
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    borderRadius: designTokens.borderRadius.full,
    textTransform: "uppercase" as const,
    whiteSpace: "nowrap" as const,
  },

  statusPillScored: {
    backgroundColor: GREEN_SOFT_BG,
    color: GREEN_INK,
  },

  statusPillError: {
    backgroundColor: projectIqTokens.rustBg,
    color: projectIqTokens.rust,
  },

  statusPillInProgress: {
    backgroundColor: AMBER_SOFT_BG,
    color: AMBER_INK,
  },
});
